import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { queryBuilds } from '@/features/builds/data/builds';
import {
  buildPipelineCounts,
  pipelineStatusDisplayOptions,
  type PipelineStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import {
  formatRangeWindowNoDataText,
  type DashboardRange,
  useDashboardDateRange,
} from '@/features/dashboard/hooks';
import { buildPipelineMetricOptions } from '@/features/dashboard/sections/dashboardTablesOverviewMetricOptions';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
} from '@/features/metrics/components/statusMetricCards';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryPipelineSummary } from '@/features/pipelines/data';
import {
  buildPipelinesChartModelOverride,
  buildPipelinesToolbarState,
} from '@/features/pipelines/hooks/pipelineChartSectionModel';
import { buildPipelineSummaryInputs } from '@/features/pipelines/hooks/pipelineSummaryInputs';
import {
  buildIsoChartDates,
  buildCountsFromBuilds,
  buildPreviousCustomWindow,
  buildPipelineMiniMetricChartDataFromBuilds,
  isBuildWithinSelectedWindow,
} from '@/features/pipelines/utils/buildMetricCharts';
import {
  buildPipelineStatusChartDataFromBuilds,
  buildPipelineStatusChartDataFromSummary,
} from '@/features/pipelines/utils/buildStatusChart';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import {
  queryKeys,
  useAccessToken,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildContainsFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { ExecutionStatusKey, MetricsRange } from '@/features/metrics/metricsHelpers';
import type { PipelinesTablePanelProps } from '@/features/pipelines/components/PipelinesTablePanel';
import type { StackedMetricsStatusChartSectionProps } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { BuildDto, BuildStatus } from '@flowqa/shared';
type UsePipelinesPageViewModelResult = {
  showPageSkeleton: boolean;
  metricSectionProps: StackedMetricsStatusChartSectionProps<PipelineStatusKey>;
  tablePanelProps: PipelinesTablePanelProps;
};
export const usePipelinesPageViewModel = (): UsePipelinesPageViewModelResult => {
  const [searchParams] = useSearchParams();
  const [range, setRange] = useState<DashboardRange>('7d');
  const [activeTab, setActiveTab] = useState<'pipelines' | 'builds'>('pipelines');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<PipelineStatusKey[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:pipelines');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isLatestMode = range === 'latest';
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;
  const showDelta = deltasEnabled && !isLatestMode && !isAllMode && previousRangeStartIso !== null;
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const statusFilterOptions = pipelineStatusDisplayOptions;
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const pipelineStatusFilters = useMemo(() => {
    if (selectedStatuses.length === 0) return [];
    const hasNoBuilds = selectedStatuses.includes('none');
    const buildStatuses = selectedStatuses.filter(
      (status): status is Exclude<PipelineStatusKey, 'none'> => status !== 'none',
    );
    const values = hasNoBuilds
      ? ([null, ...buildStatuses] as Array<Exclude<PipelineStatusKey, 'none'> | null>)
      : buildStatuses;
    return buildInFilterWithNull('latestBuildStatus', values);
  }, [selectedStatuses]);
  const selectedChartStatuses = useMemo<ExecutionStatusKey[]>(() => {
    const mapped = selectedStatuses
      .map((status): ExecutionStatusKey | null => {
        if (status === 'completed') return 'passed';
        if (status === 'failed') return 'failed';
        return null;
      })
      .filter((status): status is ExecutionStatusKey => status !== null);
    return Array.from(new Set(mapped));
  }, [selectedStatuses]);
  const hasUnsupportedChartStatusSelection =
    selectedStatuses.length > 0 && selectedChartStatuses.length === 0;
  const selectedBuildStatuses = useMemo<BuildStatus[]>(
    () => selectedStatuses.filter((status): status is BuildStatus => status !== 'none'),
    [selectedStatuses],
  );
  const hasOnlyNoBuildsSelected = selectedStatuses.length > 0 && selectedBuildStatuses.length === 0;

  const { previousSummaryFiltersKey, previousSummaryInput, summaryFiltersKey, summaryInput } =
    useMemo(
      () =>
        buildPipelineSummaryInputs({
          isLatestMode,
          search: debouncedSearchValue,
          pipelineStatusFilters,
          tagFilters,
          selectedStartDay,
          selectedEndDay,
          rangeStartIso,
          rangeEndIso,
          previousRangeStartIso,
          previousRangeEndIso,
        }),
      [
        debouncedSearchValue,
        isLatestMode,
        pipelineStatusFilters,
        previousRangeEndIso,
        previousRangeStartIso,
        rangeEndIso,
        rangeStartIso,
        selectedEndDay,
        selectedStartDay,
        tagFilters,
      ],
    );

  const pipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      summaryFiltersKey,
    ),
    enabled: Boolean(accessToken),
    queryFn: async () =>
      queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        summaryInput,
        accessToken,
      ),
  });
  const previousPipelineSummaryQuery = useQuery({
    queryKey: queryKeys.pipelineSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && !isLatestMode,
    queryFn: async () =>
      queryPipelineSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousSummaryInput ?? summaryInput,
        accessToken,
      ),
  });

  const pipelineSummary = pipelineSummaryQuery.data ?? null;
  const previousPipelineSummary = !isLatestMode
    ? (previousPipelineSummaryQuery.data ?? null)
    : null;

  const pipelineMetricOptions = useMemo(buildPipelineMetricOptions, []);

  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);
  const buildsNoRowsText = formatRangeWindowNoDataText('No builds found', range, hasCustomDates);
  const metricsRange = useMemo<MetricsRange>(
    () => (hasCustomDates ? 'all' : isLatestMode ? 'all' : range),
    [hasCustomDates, isLatestMode, range],
  );
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const chartWindowStartIso = useMemo(() => {
    if (isLatestMode || isAllMode) return null;
    if (hasCustomDates) {
      return selectedStartDay ? `${selectedStartDay}T00:00:00.000Z` : null;
    }
    return rangeStartIso;
  }, [hasCustomDates, isAllMode, isLatestMode, rangeStartIso, selectedStartDay]);
  const chartWindowEndIso = useMemo(() => {
    if (isLatestMode || isAllMode) return null;
    if (hasCustomDates) {
      return selectedEndDay ? `${selectedEndDay}T23:59:59.999Z` : null;
    }
    return rangeEndIso;
  }, [hasCustomDates, isAllMode, isLatestMode, rangeEndIso, selectedEndDay]);
  const previousChartWindow = useMemo(() => {
    if (isLatestMode || isAllMode) {
      return { startIso: null, endIso: null };
    }
    if (hasCustomDates) {
      return buildPreviousCustomWindow(selectedStartDay, selectedEndDay);
    }
    return { startIso: previousRangeStartIso, endIso: previousRangeEndIso };
  }, [
    hasCustomDates,
    isAllMode,
    isLatestMode,
    previousRangeEndIso,
    previousRangeStartIso,
    selectedEndDay,
    selectedStartDay,
  ]);

  const buildMetricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: selectedChartStatuses,
    tags: selectedTags,
    scope: 'pipeline',
    projectId: selectedProjectId,
    latestOnly: isLatestMode,
    name: debouncedSearchValue || null,
    startDay: isLatestMode ? null : hasCustomDates ? selectedStartDay : null,
    endDay: isLatestMode ? null : hasCustomDates ? selectedEndDay : null,
    enabled: Boolean(accessToken),
    refetchOnWindowFocus: false,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: selectedChartStatuses,
    tags: selectedTags,
    scope: 'pipeline',
    projectId: selectedProjectId,
    name: debouncedSearchValue || null,
    startDay: showDelta ? previousStartDay : null,
    endDay: showDelta ? previousEndDay : null,
    enabled: Boolean(accessToken && showDelta),
    refetchOnWindowFocus: false,
  });

  const chartBuildFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('pipelineName', debouncedSearchValue),
        buildInFilter('status', selectedBuildStatuses),
        tagFilters,
      ),
    [debouncedSearchValue, selectedBuildStatuses, tagFilters],
  );
  const chartBuildFiltersKey = useMemo(
    () => JSON.stringify(chartBuildFilters),
    [chartBuildFilters],
  );
  const chartBuildsQuery = useQuery({
    queryKey: queryKeys.dataGrid(
      'builds/status-chart',
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      chartBuildFiltersKey,
    ),
    enabled: Boolean(accessToken) && !isLatestMode && !hasOnlyNoBuildsSelected,
    queryFn: async (): Promise<BuildDto[]> => {
      const pageSize = 100;
      let page = 1;
      let total = Number.POSITIVE_INFINITY;
      const items: BuildDto[] = [];

      while (items.length < total) {
        const result = await queryBuilds(
          selectedProjectId,
          selectedOrganisationId ?? undefined,
          {
            page,
            pageSize,
            sorts: [{ field: 'startedAt', direction: 'asc' }],
            filters: chartBuildFilters,
          },
          accessToken,
        );
        items.push(...result.items);
        total = result.total;
        if (result.items.length === 0 || items.length >= total) break;
        page += 1;
      }

      return items;
    },
  });

  const filteredChartBuilds = useMemo(() => {
    if (!chartBuildsQuery.data) return null;
    if (chartWindowStartIso === null && chartWindowEndIso === null) {
      return chartBuildsQuery.data;
    }
    return chartBuildsQuery.data.filter((build) =>
      isBuildWithinSelectedWindow(build, chartWindowStartIso, chartWindowEndIso),
    );
  }, [chartBuildsQuery.data, chartWindowEndIso, chartWindowStartIso]);
  const previousFilteredChartBuilds = useMemo(() => {
    if (!chartBuildsQuery.data) return null;
    if (previousChartWindow.startIso === null && previousChartWindow.endIso === null) {
      return null;
    }
    return chartBuildsQuery.data.filter((build) =>
      isBuildWithinSelectedWindow(build, previousChartWindow.startIso, previousChartWindow.endIso),
    );
  }, [chartBuildsQuery.data, previousChartWindow.endIso, previousChartWindow.startIso]);
  const pipelineCounts = useMemo(
    () =>
      isLatestMode
        ? buildPipelineCounts(pipelineSummary)
        : buildCountsFromBuilds(filteredChartBuilds),
    [filteredChartBuilds, isLatestMode, pipelineSummary],
  );
  const previousPipelineCounts = useMemo(
    () =>
      isLatestMode
        ? buildPipelineCounts(previousPipelineSummary)
        : buildCountsFromBuilds(previousFilteredChartBuilds),
    [isLatestMode, previousFilteredChartBuilds, previousPipelineSummary],
  );
  const pipelineTotal = useMemo(
    () => (isLatestMode ? (pipelineSummary?.total ?? 0) : (filteredChartBuilds?.length ?? 0)),
    [filteredChartBuilds?.length, isLatestMode, pipelineSummary?.total],
  );
  const previousPipelineTotal = useMemo(
    () =>
      isLatestMode
        ? (previousPipelineSummary?.total ?? 0)
        : (previousFilteredChartBuilds?.length ?? 0),
    [isLatestMode, previousFilteredChartBuilds?.length, previousPipelineSummary?.total],
  );
  const pipelineDeltas = useMemo(
    () => (showDelta ? buildStatusDeltaMap(pipelineCounts, previousPipelineCounts) : undefined),
    [pipelineCounts, previousPipelineCounts, showDelta],
  );
  const pipelineSummaryLoading = isLatestMode
    ? pipelineSummaryQuery.isLoading
    : chartBuildsQuery.isLoading && !chartBuildsQuery.data;
  const pipelineMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: pipelineMetricOptions,
        counts: pipelineCounts,
        deltas: pipelineDeltas,
        total: pipelineTotal,
        previousCounts: previousPipelineCounts,
        previousTotal: previousPipelineTotal,
        loading: pipelineSummaryLoading,
        showDelta,
      }),
    [
      pipelineCounts,
      pipelineDeltas,
      pipelineMetricOptions,
      previousPipelineCounts,
      previousPipelineTotal,
      pipelineSummaryLoading,
      pipelineTotal,
      showDelta,
    ],
  );
  const visiblePipelineMetricCards = useMemo(
    () => pipelineMetricCards.filter((card) => card.id !== 'none'),
    [pipelineMetricCards],
  );
  const chartDates = useMemo(() => {
    if (chartWindowStartIso !== null && chartWindowEndIso !== null) {
      return buildIsoChartDates(chartWindowStartIso, chartWindowEndIso);
    }
    const labels = buildMetricsQuery.data?.metricsRangeLabels ?? [];
    const dates = buildMetricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(labels, dates);
  }, [
    buildMetricsQuery.data?.metricsRangeDates,
    buildMetricsQuery.data?.metricsRangeLabels,
    chartWindowEndIso,
    chartWindowStartIso,
  ]);
  const previousChartDates = useMemo(() => {
    if (previousChartWindow.startIso !== null && previousChartWindow.endIso !== null) {
      return buildIsoChartDates(previousChartWindow.startIso, previousChartWindow.endIso);
    }
    return [];
  }, [previousChartWindow.endIso, previousChartWindow.startIso]);
  const stackedChartData = useMemo(() => {
    if (hasOnlyNoBuildsSelected) {
      return buildPipelineStatusChartDataFromBuilds([], chartDates, selectedBuildStatuses);
    }
    if (isLatestMode) {
      const latestChartLabel = selectedEndDay ?? selectedStartDay ?? dayjs().format('YYYY-MM-DD');
      return buildPipelineStatusChartDataFromSummary(
        pipelineSummary?.statusCounts ?? {},
        latestChartLabel,
        selectedBuildStatuses,
      );
    }
    if (!filteredChartBuilds) return null;
    return buildPipelineStatusChartDataFromBuilds(
      filteredChartBuilds,
      chartDates,
      selectedBuildStatuses,
    );
  }, [
    chartDates,
    filteredChartBuilds,
    hasOnlyNoBuildsSelected,
    isLatestMode,
    pipelineSummary?.statusCounts,
    selectedBuildStatuses,
    selectedEndDay,
    selectedStartDay,
  ]);
  const currentBuildMetricChartData = useMemo(
    () =>
      isLatestMode
        ? null
        : buildPipelineMiniMetricChartDataFromBuilds(filteredChartBuilds ?? [], chartDates),
    [chartDates, filteredChartBuilds, isLatestMode],
  );
  const previousBuildMetricChartData = useMemo(
    () =>
      showDelta
        ? buildPipelineMiniMetricChartDataFromBuilds(
            previousFilteredChartBuilds ?? [],
            previousChartDates,
          )
        : null,
    [previousChartDates, previousFilteredChartBuilds, showDelta],
  );
  const summaryPassRate = useMemo(
    () =>
      isLatestMode
        ? (pipelineSummary?.averageExecutionPassRate ?? null)
        : (currentBuildMetricChartData?.passRate ?? null),
    [
      currentBuildMetricChartData?.passRate,
      isLatestMode,
      pipelineSummary?.averageExecutionPassRate,
    ],
  );
  const previousSummaryPassRate = useMemo(
    () => (showDelta ? (previousBuildMetricChartData?.passRate ?? null) : null),
    [previousBuildMetricChartData?.passRate, showDelta],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: buildMetricsQuery.data ?? null,
        previousMetrics: showDelta ? (previousMetricsQuery.data ?? null) : null,
        frequencyOverride: currentBuildMetricChartData?.frequencyPerDay ?? null,
        previousFrequencyOverride: previousBuildMetricChartData?.frequencyPerDay ?? null,
        avgDurationOverride: currentBuildMetricChartData?.avgDurationSeconds ?? null,
        previousAvgDurationOverride: previousBuildMetricChartData?.avgDurationSeconds ?? null,
        passRateOverride: summaryPassRate,
        previousPassRateOverride: previousSummaryPassRate,
        thresholds: thresholds.pipelines,
        showDelta,
      }),
    [
      buildMetricsQuery.data,
      currentBuildMetricChartData?.avgDurationSeconds,
      currentBuildMetricChartData?.frequencyPerDay,
      previousMetricsQuery.data,
      previousBuildMetricChartData?.avgDurationSeconds,
      previousBuildMetricChartData?.frequencyPerDay,
      previousSummaryPassRate,
      showDelta,
      summaryPassRate,
      thresholds.pipelines,
    ],
  );
  const lineChartLabels = buildMetricsQuery.data?.metricsRangeLabels ?? [];
  const successRateChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    if (!isLatestMode) {
      if (filteredChartBuilds === null) return chartBuildsQuery.isFetched ? [] : null;
      return currentBuildMetricChartData?.passRateChartData ?? [];
    }
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    chartBuildsQuery.isFetched,
    buildMetricsQuery.data?.series.filteredPassRate,
    buildMetricsQuery.isFetched,
    chartDates,
    currentBuildMetricChartData?.passRateChartData,
    filteredChartBuilds,
    hasUnsupportedChartStatusSelection,
    isLatestMode,
    lineChartLabels,
  ]);
  const frequencyChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    if (!isLatestMode) {
      if (filteredChartBuilds === null) return chartBuildsQuery.isFetched ? [] : null;
      return currentBuildMetricChartData?.frequencyChartData ?? [];
    }
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    chartBuildsQuery.isFetched,
    buildMetricsQuery.data?.series.executionTrendMetrics,
    buildMetricsQuery.isFetched,
    chartDates,
    currentBuildMetricChartData?.frequencyChartData,
    filteredChartBuilds,
    hasUnsupportedChartStatusSelection,
    isLatestMode,
    lineChartLabels,
  ]);
  const durationChartData = useMemo(() => {
    if (hasUnsupportedChartStatusSelection) return [];
    if (!isLatestMode) {
      if (filteredChartBuilds === null) return chartBuildsQuery.isFetched ? [] : null;
      return currentBuildMetricChartData?.durationChartData ?? [];
    }
    const series = buildLineChartSeries(
      buildMetricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return buildMetricsQuery.isFetched ? [] : null;
  }, [
    chartBuildsQuery.isFetched,
    buildMetricsQuery.data?.series.executionDurationAverage,
    buildMetricsQuery.isFetched,
    chartDates,
    currentBuildMetricChartData?.durationChartData,
    filteredChartBuilds,
    hasUnsupportedChartStatusSelection,
    isLatestMode,
    lineChartLabels,
  ]);

  const metricsStartDay = useMemo(() => chartDates[0] ?? null, [chartDates]);
  const metricsEndDay = useMemo(
    () => (chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null),
    [chartDates],
  );
  const pickerStartDay = useMemo(
    () =>
      selectedStartDay ??
      (range !== 'latest' && range !== 'all'
        ? (rangeStartIso?.slice(0, 10) ?? null)
        : metricsStartDay),
    [metricsStartDay, range, rangeStartIso, selectedStartDay],
  );
  const pickerEndDay = useMemo(
    () =>
      selectedEndDay ??
      (range !== 'latest' && range !== 'all' ? (rangeEndIso?.slice(0, 10) ?? null) : metricsEndDay),
    [metricsEndDay, range, rangeEndIso, selectedEndDay],
  );

  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const resetRangeDates = useCallback((next: CloneDateQuickFilter) => {
    setRange(next);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const chartToolbarQuickFilter: CloneDateQuickFilter =
    range === '14d' || range === '30d' || range === 'latest' ? range : '7d';
  const forcedLoadState = searchParams.get('loadState');
  const forcedPageLoadState = searchParams.get('pageLoadState');
  const showForcedLoadingState = forcedLoadState === 'loading';
  const showPageSkeleton =
    forcedPageLoadState === 'loading' ||
    (Boolean(accessToken) && pipelineSummaryQuery.isLoading && !pipelineSummary);
  const toolbarState = useMemo(
    () =>
      buildPipelinesToolbarState({
        search: debouncedSearchValue,
        setSearch: setDebouncedSearchValue,
        selectedStatuses,
        setSelectedStatuses,
        selectedTags,
        setSelectedTags,
        noRowsText: buildsNoRowsText,
        dateQuickFilter: chartToolbarQuickFilter,
        onDateQuickFilterChange: resetRangeDates,
        startDay: selectedStartDay,
        endDay: selectedEndDay,
        onFromDateChange: handleStartDayChange,
        onToDateChange: handleEndDayChange,
      }),
    [
      buildsNoRowsText,
      chartToolbarQuickFilter,
      debouncedSearchValue,
      handleEndDayChange,
      handleStartDayChange,
      resetRangeDates,
      selectedEndDay,
      selectedStartDay,
      selectedStatuses,
      selectedTags,
      setSelectedTags,
    ],
  );
  const modelOverride = useMemo(
    () =>
      buildPipelinesChartModelOverride({
        stackedChartData,
        passRateChartData: successRateChartData,
        frequencyChartData,
        durationChartData,
        metricValueItems,
      }),
    [
      durationChartData,
      frequencyChartData,
      metricValueItems,
      stackedChartData,
      successRateChartData,
    ],
  );

  return {
    showPageSkeleton,
    metricSectionProps: {
      title: 'Status',
      searchAriaLabel: 'Search pipelines',
      noRowsBaseText: 'No builds found',
      scope: 'pipeline',
      projectId: selectedProjectId,
      availableTags: tagOptions.map((option) => option.value),
      statusFilterLabel: 'Status',
      metricCards: visiblePipelineMetricCards,
      statusOptions: statusFilterOptions,
      toolbarState,
      modelOverride,
      loading: showForcedLoadingState,
      suppressAnimation: false,
    },
    tablePanelProps: {
      activeTab,
      onActiveTabChange: setActiveTab,
      selectedProjectId: selectedProjectId ?? null,
      selectedOrganisationId: selectedOrganisationId ?? null,
      deltasEnabled,
      pipelineSearchValue: debouncedSearchValue,
      onPipelineSearchChange: setDebouncedSearchValue,
      pipelineFilterStatuses: selectedStatuses,
      onPipelineFilterStatusesChange: setSelectedStatuses,
      pipelineFilterTags: selectedTags,
      onPipelineFilterTagsChange: setSelectedTags,
      pipelineDateQuickFilter: chartToolbarQuickFilter,
      onPipelineDateQuickFilterChange: resetRangeDates,
      pipelineFromDate: selectedStartDay ? dayjs(selectedStartDay) : null,
      pipelineToDate: selectedEndDay ? dayjs(selectedEndDay) : null,
      onPipelineFromDateChange: (next) => {
        handleStartDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
      onPipelineToDateChange: (next) => {
        handleEndDayChange(next ? next.format('YYYY-MM-DD') : null);
      },
    },
  };
};
